import React, { useState, useEffect, useRef } from "react";
import { saveAs } from "file-saver";
import {
  Box,
  Flex,
  Text,
  Button,
  Center,
  Container,
  VStack,
  Heading,
  Grid,
  Icon,
  Input,
  SimpleGrid,
  Badge,
} from "@chakra-ui/react";
import apiClient from "Configuration/apiClient";
import API_CONFIG from "../../Configuration/apiConfig";
import {
  FiDownload,
  FiCalendar,
  FiFileText,
} from "react-icons/fi";
import { BsGraphUp } from "react-icons/bs";
import { TbReportAnalytics } from "react-icons/tb";
import { handleStoresExport } from "./StoresRawDataReport";
import { handleSoviExport } from "./SoviReport";
import { handleManualStoreAuditExport } from "./ManualStoreAuditReport";
import { dedupeExactRows } from "../../Utils/dedupeUtils";

const REPORT_TYPE_ALLOCATION   = "allocation";
const REPORT_TYPE_FRESHNESS    = "freshness";
const REPORT_TYPE_STORES       = "stores";
const REPORT_TYPE_SOVI         = "MT";
const REPORT_TYPE_MANUAL_AUDIT = "manualAudit";



export default function DownloadExcel({
  asmData       = null,
  channelData   = null,
  subregionData = null,
  bnlData       = null,
  bntlData      = null,
}) {
  const userRole = localStorage.getItem("userRole") || sessionStorage.getItem("userRole") || "";
  const isCocaColaUser = userRole === "CocaColaUSER";

  const [loading, setLoading] = useState(false);
  const [loadingIndex, setLoadingIndex] = useState(null);
  const [reportType, setReportType] = useState(REPORT_TYPE_ALLOCATION);

  const now   = new Date();
  const year  = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day   = String(now.getDate()).padStart(2, "0");
  const fromDateDefault = `${year}-${month}-01`;
  const toDateDefault   = `${year}-${month}-${day}`;

  const [fromDate, setFromDate] = useState(fromDateDefault);
  const [toDate,   setToDate]   = useState(toDateDefault);


  const fromDateRef = useRef(fromDate);
  const toDateRef   = useRef(toDate);
  useEffect(() => { fromDateRef.current = fromDate; }, [fromDate]);
  useEffect(() => { toDateRef.current   = toDate;   }, [toDate]);

  const labels = [
    "Total Shops",
    "Total RED Score",
    "Cooler Score",
    "Availability",
    "Activation",
    "Cooler Bonus",
  ];

  const [recentReports, setRecentReports] = useState([]);


  const buildPastThreeMonths = (anchorDate) => {
    const result = [];
    for (let i = 1; i <= 3; i++) {
      const d = new Date(anchorDate.getFullYear(), anchorDate.getMonth() - i, 1);
      result.push({
        month: d.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
        size: `${(100 + Math.random() * 2).toFixed(1)} KB`,
        status: "completed",
        dateValue: d,
        reportType: REPORT_TYPE_ALLOCATION, 
      });
    }
    return result;
  };

  const addToRecentReports = (from, to, type) => {
    const label =
      type === REPORT_TYPE_FRESHNESS    ? "Outlet Report"
      : type === REPORT_TYPE_STORES     ? "Stores Raw Data Report"
      : type === REPORT_TYPE_SOVI       ? "MT Report"
      : type === REPORT_TYPE_MANUAL_AUDIT ? "Manual Store Audit Report"
      : "Red Allocation Report";

    const entry = {
      month: `${label} (${from} → ${to})`,
      size: "—",
      status: "completed",
      dateValue: new Date(from),
      reportType: type,
      fromOverride: from,
      toOverride: to,
    };

    setRecentReports((prev) => [entry, ...prev].slice(0, 6));
  };

  useEffect(() => {
    setRecentReports(buildPastThreeMonths(new Date()));
  }, []);

  
  const handleDownloadReport = async (report) => {
    const from = report.fromOverride
      || new Date(report.dateValue.getFullYear(), report.dateValue.getMonth(), 1)
           .toISOString().split("T")[0];
    const to = report.toOverride
      || new Date(report.dateValue.getFullYear(), report.dateValue.getMonth() + 1, 0)
           .toISOString().split("T")[0];

    const type = report.reportType ?? REPORT_TYPE_ALLOCATION;

    if (type === REPORT_TYPE_FRESHNESS) {
      await handleFreshnessExport(from, to);
    } else if (type === REPORT_TYPE_STORES) {
      await handleStoresExport(from, to, setLoading);
    } else if (type === REPORT_TYPE_SOVI) {
      await handleSoviExport(from, to, setLoading);
    } else if (type === REPORT_TYPE_MANUAL_AUDIT) {
      await handleManualStoreAuditExport(from, to, setLoading);
    } else {
      await handleAllocationExport(from, to);
    }
  };

  
  const parseDatePayload = (dateStr) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    return { date: d, month: m, year: y };
  };


  const buildAllocationWorkbook = async (
    asmJson, channelJson, subregionJson, bnlJson, bntlJson, outletJson
  ) => {
    const ExcelJSModule = await import("exceljs/dist/exceljs.min.js").catch(
      async () => await import("exceljs")
    );
    const ExcelJS = ExcelJSModule.default || ExcelJSModule;
    const wb = new ExcelJS.Workbook();

    const asmHeaderFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFF8000" } };
    const asmHeaderFont = { bold: true, color: { argb: "FFFFFFFF" } };
    const soHeaderFill  = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFE6CC" } };

 
    const wsA = wb.addWorksheet("RED SCORE");
    for (let i = 0; i < labels.length; i++) {
      const cell = wsA.getCell(2 + i, 1);
      cell.value = labels[i];
      cell.font  = { bold: true };
      cell.alignment = { horizontal: "left", vertical: "center" };
    }
    const asmSummary = asmJson?.asmLevelReport || {};
    const overallAsm = [
      asmSummary.totalShops ?? "",
      asmSummary.totalRedScore ?? "",
      asmSummary.coolerScore ?? "",
      asmSummary.availability ?? "",
      asmSummary.activation ?? "",
      asmSummary.coolerBonus ?? "",
    ];
    wsA.getCell(1, 2).value = "Overall";
    wsA.getCell(1, 2).font  = { bold: true };
    wsA.getCell(1, 2).alignment = { textRotation: 90, horizontal: "center", vertical: "bottom" };
    overallAsm.forEach((v, i) => {
      wsA.getCell(2 + i, 2).value = v;
      wsA.getCell(2 + i, 2).alignment = { horizontal: "center", vertical: "center" };
    });

    let col = 3;
    for (const asmBlock of (Array.isArray(asmJson?.asmSoData) ? asmJson.asmSoData : [])) {
      const asm = asmBlock.asmSummary || {};
      const h = wsA.getCell(1, col);
      h.value = asm.asmName ?? "";
      h.alignment = { textRotation: 90, horizontal: "center", vertical: "bottom", wrapText: true };
      h.fill = asmHeaderFill; h.font = asmHeaderFont;
      h.border = { top:{style:"thin"}, left:{style:"thin"}, bottom:{style:"thin"}, right:{style:"thin"} };
      [asm.totalStores, asm.redScoreAvg, asm.coolerScoreAvg, asm.availabilityScoreAvg, asm.activationScoreAvg, asm.coolerBonusAvg]
        .forEach((v, i) => {
          const c = wsA.getCell(2 + i, col);
          c.value = v ?? ""; c.alignment = { horizontal: "center", vertical: "center" };
          c.border = { top:{style:"thin"}, left:{style:"thin"}, bottom:{style:"thin"}, right:{style:"thin"} };
        });
      wsA.getColumn(col).width = 6; col++;
      for (const so of (Array.isArray(asmBlock.soDetails) ? asmBlock.soDetails : [])) {
        const sh = wsA.getCell(1, col);
        sh.value = so.soName ?? "";
        sh.alignment = { textRotation: 90, horizontal: "center", vertical: "bottom", wrapText: true };
        sh.fill = soHeaderFill; sh.font = { bold: false, color: { argb: "FF000000" } };
        sh.border = { top:{style:"thin"}, left:{style:"thin"}, bottom:{style:"thin"}, right:{style:"thin"} };
        [so.totalStores, so.redScoreAvg, so.coolerScoreAvg, so.availabilityScoreAvg, so.activationScoreAvg, so.coolerBonusAvg]
          .forEach((v, i) => {
            const c = wsA.getCell(2 + i, col);
            c.value = v ?? ""; c.alignment = { horizontal: "center", vertical: "center" };
            c.border = { top:{style:"thin"}, left:{style:"thin"}, bottom:{style:"thin"}, right:{style:"thin"} };
          });
        wsA.getColumn(col).width = 6; col++;
      }
    }
    wsA.getRow(1).height = 120; wsA.getColumn(1).width = 24; wsA.getColumn(2).width = 12;
    wsA.views = [{ state: "frozen", ySplit: 1 }];


    const wsC = wb.addWorksheet("CHANNELS");
    for (let i = 0; i < labels.length; i++) {
      const cell = wsC.getCell(2 + i, 1);
      cell.value = labels[i]; cell.font = { bold: true };
      cell.alignment = { horizontal: "left", vertical: "center" };
    }
    const channelSummary = channelJson?.summary || {};
    wsC.getCell(1, 2).value = "Overall"; wsC.getCell(1, 2).font = { bold: true };
    wsC.getCell(1, 2).alignment = { textRotation: 90, horizontal: "center", vertical: "bottom" };
    [channelSummary.totalShops, channelSummary.totalRedScore, channelSummary.coolerScore,
     channelSummary.availability, channelSummary.activation, channelSummary.coolerBonus]
      .forEach((v, i) => { wsC.getCell(2 + i, 2).value = v ?? ""; wsC.getCell(2 + i, 2).alignment = { horizontal: "center", vertical: "center" }; });
    let ccol = 3;
    for (const ch of (Array.isArray(channelJson?.channels) ? channelJson.channels : [])) {
      const vals = ch.channels ?? {};
      const chHdr = wsC.getCell(1, ccol);
      chHdr.value = ch.channelName ?? "";
      chHdr.alignment = { textRotation: 90, horizontal: "center", vertical: "bottom", wrapText: true };
      chHdr.fill = asmHeaderFill; chHdr.font = asmHeaderFont;
      chHdr.border = { top:{style:"thin"}, left:{style:"thin"}, bottom:{style:"thin"}, right:{style:"thin"} };
      [vals.totalShops, vals.totalRedScore, vals.coolerScore, vals.availability, vals.activation, vals.coolerBonus]
        .forEach((v, i) => {
          const c = wsC.getCell(2 + i, ccol);
          c.value = v ?? ""; c.alignment = { horizontal: "center", vertical: "center" };
          c.border = { top:{style:"thin"}, left:{style:"thin"}, bottom:{style:"thin"}, right:{style:"thin"} };
        });
      wsC.getColumn(ccol).width = 9; ccol++;
    }
    wsC.getRow(1).height = 60; wsC.getColumn(1).width = 24; wsC.getColumn(2).width = 12;
    wsC.views = [{ state: "frozen", ySplit: 1 }];

    const wsS = wb.addWorksheet("REGIONWISE SCORE");
    for (let i = 0; i < labels.length; i++) {
      const cell = wsS.getCell(2 + i, 1);
      cell.value = labels[i]; cell.font = { bold: true };
      cell.alignment = { horizontal: "left", vertical: "center" };
    }
    const subSummary = subregionJson?.summary || {};
    wsS.getCell(1, 2).value = "Overall"; wsS.getCell(1, 2).font = { bold: true };
    wsS.getCell(1, 2).alignment = { textRotation: 90, horizontal: "center", vertical: "bottom" };
    [subSummary.totalShops, subSummary.totalRedScore, subSummary.coolerScore,
     subSummary.availability, subSummary.activation, subSummary.coolerBonus]
      .forEach((v, i) => { wsS.getCell(2 + i, 2).value = v ?? ""; wsS.getCell(2 + i, 2).alignment = { horizontal: "center", vertical: "center" }; });
    let scol = 3;
    for (const sub of (Array.isArray(subregionJson?.subregions) ? subregionJson.subregions : [])) {
      const vals = sub.summary ?? {};
      const sh = wsS.getCell(1, scol);
      sh.value = sub.subRegionName ?? "";
      sh.alignment = { textRotation: 90, horizontal: "center", vertical: "bottom", wrapText: true };
      sh.fill = asmHeaderFill; sh.font = asmHeaderFont;
      sh.border = { top:{style:"thin"}, left:{style:"thin"}, bottom:{style:"thin"}, right:{style:"thin"} };
      [vals.totalShops, vals.totalRedScore, vals.coolerScore, vals.availability, vals.activation, vals.coolerBonus]
        .forEach((v, i) => {
          const c = wsS.getCell(2 + i, scol);
          c.value = v ?? ""; c.alignment = { horizontal: "center", vertical: "center" };
          c.border = { top:{style:"thin"}, left:{style:"thin"}, bottom:{style:"thin"}, right:{style:"thin"} };
        });
      wsS.getColumn(scol).width = 9; scol++;
    }
    wsS.getRow(1).height = 60; wsS.getColumn(1).width = 24; wsS.getColumn(2).width = 12;
    wsS.views = [{ state: "frozen", ySplit: 1 }];

    
    const buildMdcSheet = (sheetName, jsonData) => {
      const ws = wb.addWorksheet(sheetName);
      for (let i = 0; i < labels.length; i++) {
        const cell = ws.getCell(2 + i, 1);
        cell.value = labels[i]; cell.font = { bold: true };
        cell.alignment = { horizontal: "left", vertical: "center" };
      }
      const totalSummary = jsonData?.totalSummary || {};
      ws.getCell(1, 2).value = "Overall"; ws.getCell(1, 2).font = { bold: true };
      ws.getCell(1, 2).alignment = { textRotation: 90, horizontal: "center", vertical: "bottom" };
      [totalSummary.totalShops, totalSummary.totalRedScore, totalSummary.coolerScore,
       totalSummary.availability, totalSummary.activation, totalSummary.coolerBonus]
        .forEach((v, i) => { ws.getCell(2 + i, 2).value = v ?? ""; ws.getCell(2 + i, 2).alignment = { horizontal: "center", vertical: "center" }; });
      let bcol = 3;
      for (const mdcBlock of (Array.isArray(jsonData?.mdcData) ? jsonData.mdcData : [])) {
        const mdc = mdcBlock.asmSummary || {};
        const h = ws.getCell(1, bcol);
        h.value = mdcBlock.distributorName ?? "";
        h.alignment = { textRotation: 90, horizontal: "center", vertical: "bottom", wrapText: true };
        h.fill = asmHeaderFill; h.font = asmHeaderFont;
        h.border = { top:{style:"thin"}, left:{style:"thin"}, bottom:{style:"thin"}, right:{style:"thin"} };
        [mdc.totalShops, mdc.totalRedScore, mdc.coolerScore, mdc.availability, mdc.activation, mdc.coolerBonus]
          .forEach((v, i) => {
            const c = ws.getCell(2 + i, bcol);
            c.value = v ?? ""; c.alignment = { horizontal: "center", vertical: "center" };
            c.border = { top:{style:"thin"}, left:{style:"thin"}, bottom:{style:"thin"}, right:{style:"thin"} };
          });
        ws.getColumn(bcol).width = 15; bcol++;
        for (const so of (Array.isArray(mdcBlock.soDetails) ? mdcBlock.soDetails : [])) {
          const sh = ws.getCell(1, bcol);
          sh.value = so.soName ?? "";
          sh.alignment = { textRotation: 90, horizontal: "center", vertical: "bottom", wrapText: true };
          sh.fill = soHeaderFill; sh.font = { bold: false, color: { argb: "FF000000" } };
          sh.border = { top:{style:"thin"}, left:{style:"thin"}, bottom:{style:"thin"}, right:{style:"thin"} };
          [so.totalStores, so.redScoreAvg, so.coolerScoreAvg, so.availabilityScoreAvg, so.activationScoreAvg, so.coolerBonusAvg]
            .forEach((v, i) => {
              const c = ws.getCell(2 + i, bcol);
              c.value = v ?? ""; c.alignment = { horizontal: "center", vertical: "center" };
              c.border = { top:{style:"thin"}, left:{style:"thin"}, bottom:{style:"thin"}, right:{style:"thin"} };
            });
          ws.getColumn(bcol).width = 15; bcol++;
        }
      }
      ws.getRow(1).height = 120; ws.getColumn(1).width = 24; ws.getColumn(2).width = 12;
      ws.views = [{ state: "frozen", ySplit: 1 }];
    };
    buildMdcSheet("BNL",  bnlJson);
    buildMdcSheet("BNTL", bntlJson);

   
    const wsO = wb.addWorksheet("outlet_level_data");

    
    const activationKeys = [];
    (Array.isArray(outletJson) ? outletJson : []).forEach((row) => {
      const results = row["Activation Results"];
      if (results && typeof results === "object") {
        Object.keys(results).forEach((k) => {
          if (!activationKeys.includes(k)) activationKeys.push(k);
        });
      }
    });
    const hasActivationResults = activationKeys.length > 0;
    const activationCols = hasActivationResults
      ? activationKeys
      : ["Q19","Q20","Q21","Q22","Q23","Q24","Q25","Q26","Q27","Q28","Q29","Q30","Q31","Q32","Q33","Q34"];

    const outletHeaders = [
      "Year","Month","Audit Date","Region","Sub-Region","Town Code","Shop No","Customer",
      "Outlet Type","Q4f. Outlet Class","Outlet Type-Class","Town Name","Name of outlet",
      "Address","Owner","Phone No.","Town Name with Market","Market","New Market",
      "MDC Code","MDC Name","Regional Sales Manager","Area Sales Manager","Sales Man",
      "Sales Officer","Q7i. Is Fountain Available ?","Q7. Is Coca-Cola Visicooler present?",
      "COOLER","Q9b. Purity Score","Q7a. Location","Q7c. Planogram","Q7f. Cooler Caser",
      "Purity Level (100-Impurity)","Impurity",
      "Q9b. RGB Space Occupied by KO Product-Shelf 1","Q9b. RGB Space Occupied by KO Product-Shelf 2",
      "Q9b. RGB Space Occupied by KO Product-Shelf 3","Q9b. RGB Space Occupied by KO Product-Shelf 4",
      "Q9b. RGB Space Occupied by KO Product-Shelf 5","Q9b. RGB Space Occupied by KO Product-Shelf 6",
      "Q9b. RGB Space Occupied by KO Product-Shelf 7","Q9b. RGB Space Occupied by KO Product-Shelf 8",
      "Q9b. RGB Space Occupied by KO Product-Shelf 9","Q9b. RGB Space Occupied by KO Product-Shelf 10",
      "Q9b. Purity Score","AVAILABILITY",
      "Q10.1 Coke 250 ml RGB","Q10.2 Sprite 250 ml RGB","Q10.3 Fanta 250 ml RGB",
      "Q10.5 Kinley Soda 250 ml RGB","Q10.6 Coke 175 ml RGB","Q10a.6 Coke Zero 250ml PET","Q10.7 Sprite 175 ml RGB",
      "Q10.8 Fanta 175 ml RGB","Q10.9 Coke 250 ml PET","Q10.10 Sprite 250 ml PET",
      "Q10.11 Fanta Orange 250 ml PET","Q10.12 Fanta Lemon 250 ml PET","Q10.13 Kinley Soda",
      "Q12a.1 Coke ASSP 500ml","Q12a.2 Sprite ASSP 500ml","Q12a.3 Fanta Orange ASSP 500ml",
      "Q12a.4 Coke Zero 500ml PET","Q12a.6 Kinley Soda 500/600/750ml","Q12a.7 Kinley Water 500/600/750ml",
      "Q13.1 Coke 1000 ML","Q13.2 Sprite 1000 ML","Q13.3 Fanta 1000 ML","Q13.4 Kinley Water 1000 ML",
      "Q14.1 Coke FC1500","Q14.2 Sprite FC1500","Q14.3 Fanta FC1500",
      "Q15.1 Coke FC2000","Q15.2 Sprite FC2000","Q15.3 Fanta Orange FC2000","Q15.4 Fanta Lemon FC2000",
      "Q16.1 Coke FC2250","Q16.2 Sprite FC2250","Q16.3 Fanta Orange FC2250","Q16.4 Fanta Lemon FC2250",
      "ACTIVATION", ...activationCols,
      "TOTAL BONUS","Purity_Bonus","Total Score","MUST HAVE SKUS COUNT",
    ];
    const outletYellowFill = { type:"pattern", pattern:"solid", fgColor:{ argb:"FFFFFF00" } };
    outletHeaders.forEach((header, idx) => {
      const cell = wsO.getCell(1, idx + 1);
      cell.value = header; cell.fill = outletYellowFill; cell.font = { bold: true };
      cell.alignment = { horizontal:"center", vertical:"middle", wrapText:true };
      cell.border = { top:{style:"thin"}, left:{style:"thin"}, bottom:{style:"thin"}, right:{style:"thin"} };
      wsO.getColumn(idx + 1).width = 18;
    });
    (Array.isArray(outletJson) ? outletJson : []).forEach((row) => {
      const shelfCols = Array.from(
        { length: 10 },
        (_, s) => row[`Q9b. RGB Space Occupied by KO Product-Shelf ${s + 1}`] ?? row[`shelf_${s+1}`] ?? row[`Shelf ${s+1}`] ?? 0
      );
      const activationValues = activationCols.map((key) =>
        hasActivationResults ? (row["Activation Results"]?.[key] ?? "") : (row[key] ?? 0)
      );
      wsO.addRow([
        row["Year"]??"", row["Month"]??"", row["Audit Date"]??"", row["Region"]??"", row["Sub-Region"]??"",
        row["Town Code"]??"", row["Shop No"]??"", row["Customer"]??"", row["Outlet Type"]??"",
        row["Q4f. Outlet Class"]??"", row["Outlet Type-Class"]??"",
        row["town_name"]??row["Town Name"]??"", row["store_name"]??"", row["store_address"]??"",
        row["Owner"]??"", row["telephone_no"]??"", row["town_name"]??row["Town Name with Market"]??"",
        row["Market"]??"", row["New Market"]??"", row["district_mdc_code"]??row["MDC Code"]??"",
        row["distributor_name"]??row["MDC Name"]??"",
        row["rsm_name"]??"", row["asm_name"]??"", row["ps_ds_name"]??"", row["so_se_name"]??"",
        row["Q7i. Is Fountain Available ?"]??"", row["remarks_for_visicooler"]??row["Q7. Is Coca-Cola Visicooler present?"]??"",
        row["cooler_score"]??0, row["purity_score"]??0, row["cooler_position"]??row["Q7a. Location"]??0,
        row["planogram"]??row["Q7c. Planogram"]??0, row["Q7f. Cooler Caser"]??0,
        row["coolerpuritypercent"]??row["Purity Level (100-Impurity)"]??0,
        row["coolerimpuritypercent"]??row["Impurity"]??0,
        ...shelfCols,
        row["purity_score"]??0, row["availability_score"]??0,
        row["Q10.1 Coke 250 ml RGB"]??row["Q10.1"]??0, row["Q10.2 Sprite 250 ml RGB"]??row["Q10.2"]??0,
        row["Q10.3 Fanta 250 ml RGB"]??row["Q10.3"]??0, row["Q10.5 Kinley Soda 250 ml RGB"]??row["Q10.5"]??0,
        row["Q10.6 Coke 175 ml RGB"]??row["Q10.6"]??0, row["Q10a.6 Coke Zero 250ml PET"]??row["Q10a.6"]??0,
        row["Q10.7 Sprite 175 ml RGB"]??row["Q10.7"]??0,
        row["Q10.8 Fanta 175 ml RGB"]??row["Q10.8"]??0, row["Q10.9 Coke 250 ml PET"]??row["Q10.9"]??0,
        row["Q10.10 Sprite 250 ml PET"]??row["Q10.10"]??0, row["Q10.11 Fanta Orange 250 ml PET"]??row["Q10.11"]??0,
        row["Q10.12 Fanta Lemon 250 ml PET"]??row["Q10.12"]??0, row["Q10.13 Kinley Soda"]??row["Q10.13"]??0,
        row["Q12a.1 Coke ASSP 500ml"]??row["Q12a.1"]??0, row["Q12a.2 Sprite ASSP 500ml"]??row["Q12a.2"]??0,
        row["Q12a.3 Fanta Orange ASSP 500ml"]??row["Q12a.3"]??0,
        row["Q12a.4 Coke Zero 500ml PET"]??row["Q12a.4"]??0,
        row["Q12a.6 Kinley Soda 500/600/750ml"]??row["Q12a.6"]??0,
        row["Q12a.7 Kinley Water 500/600/750ml"]??row["Q12a.7"]??0,
        row["Q13.1 Coke 1000 ML"]??row["Q13.1"]??0, row["Q13.2 Sprite 1000 ML"]??row["Q13.2"]??0,
        row["Q13.3 Fanta 1000 ML"]??row["Q13.3"]??0, row["Q13.4 Kinley Water 1000 ML"]??row["Q13.4"]??0,
        row["Q14.1 Coke FC1500"]??row["Q14.1"]??0, row["Q14.2 Sprite FC1500"]??row["Q14.2"]??0,
        row["Q14.3 Fanta FC1500"]??row["Q14.3"]??0,
        row["Q15.1 Coke FC2000"]??row["Q15.1"]??0, row["Q15.2 Sprite FC2000"]??row["Q15.2"]??0,
        row["Q15.3 Fanta Orange FC2000"]??row["Q15.3"]??0, row["Q15.4 Fanta Lemon FC2000"]??row["Q15.4"]??0,
        row["Q16.1 Coke FC2250"]??row["Q16.1"]??0, row["Q16.2 Sprite FC2250"]??row["Q16.2"]??0,
        row["Q16.3 Fanta Orange FC2250"]??row["Q16.3"]??0, row["Q16.4 Fanta Lemon FC2250"]??row["Q16.4"]??0,
        row["activation_score"]??0, ...activationValues,
        row["TOTAL BONUS"]??0, row["Purity_Bonus"]??0, row["Total Score"]??0,
        row["sku_count"]??row["MUST HAVE SKUS COUNT"]??0,
      ]);
    });
    wsO.getRow(1).height = 40;
    wsO.views = [{ state: "frozen", ySplit: 1 }];

    return wb;
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RED ALLOCATION – export handler
  // ═══════════════════════════════════════════════════════════════════════════
  const handleAllocationExport = async (overrideFrom, overrideTo) => {
    setLoading(true);
    const from = overrideFrom ?? fromDateRef.current;
    const to   = overrideTo   ?? toDateRef.current;
    const body = { from: parseDatePayload(from), to: parseDatePayload(to) };

    try {
      let asmJson = asmData;
      if (!asmJson) {
        const res = await apiClient.post(`/red-score/api/report`, body);
        if (res.status !== 200) throw new Error("ASM API fetch failed: " + res.status);
        asmJson = res.data;
      }
      let channelJson = channelData;
      if (!channelJson) {
        const res = await apiClient.post(`/channel-level-report`, body);
        if (res.status !== 200) throw new Error("Channel API fetch failed: " + res.status);
        channelJson = res.data;
      }
      let subJson = subregionData;
      if (!subJson) {
        const res = await apiClient.post(`/api/subregion-report`, body);
        if (res.status !== 200) throw new Error("Subregion API fetch failed: " + res.status);
        subJson = res.data;
      }
      let bnlJson = bnlData;
      if (!bnlJson) {
        const res = await apiClient.post(`/api/report/bnl`, body);
        if (res.status !== 200) throw new Error("BNL API fetch failed: " + res.status);
        bnlJson = res.data;
      }
      let bntlJson = bntlData;
      if (!bntlJson) {
        const res = await apiClient.post(`/api/report/bntl`, body);
        if (res.status !== 200) throw new Error("BNTL API fetch failed: " + res.status);
        bntlJson = res.data;
      }
      const resOutlet = await apiClient.post(`/api/outlet-report`, body);
      if (resOutlet.status !== 200) throw new Error("Outlet API fetch failed: " + resOutlet.status);
      const outletJson = resOutlet.data;

      const wb = await buildAllocationWorkbook(asmJson, channelJson, subJson, bnlJson, bntlJson, outletJson);
      const buffer = await wb.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], { type: "application/octet-stream" }),
        `red_allocation_report_${from.replace(/-/g, "")}_to_${to.replace(/-/g, "")}.xlsx`
      );

      // Only add to recent reports when triggered from main button (no overrides)
      if (!overrideFrom && !overrideTo) addToRecentReports(from, to, REPORT_TYPE_ALLOCATION);
    } catch (err) {
      console.error("Allocation export error:", err);
      alert("Failed to generate Excel: " + (err.message || err));
    } finally {
      setLoading(false);
    }
  };


  const handleFreshnessExport = async (overrideFrom, overrideTo) => {
    setLoading(true);
    const from = overrideFrom ?? fromDateRef.current;
    const to   = overrideTo   ?? toDateRef.current;
    const body = { from: parseDatePayload(from), to: parseDatePayload(to) };

    try {
      const res = await apiClient.post(
        `${API_CONFIG.ENDPOINTS.RED_FRESHNESS.REPORT}`,
        body
      );
      if (res.status !== 200) throw new Error("Red Freshness API fetch failed: " + res.status);

      const list   = Array.isArray(res.data) ? res.data : [res.data];
      const mapped = list.map((item) => ({
        shopNo:      item.SHOPNO      || "",
        customer:    item.CUSTOMER    || "",
        outletName:  item["OUTLET NAME"] || "",
        address:     item.ADDRESS     || "",
        region:      item.REGION      || "",
        subRegion:   item["SUB REGION"] || "",
        outletType:  item["OUTLET TYPE"] || "",
        class:       item.CLASS       || "",
        brand:       item.BRAND       || "",
        packaging:   item.PACKAGING   || "",
        sku:         item.SKU         || "",
        surveyDate:  item["SURVEY DATE"] || "",
        mfd:         item.MFD         || "",
        expiryDate:  item["EXPIRY DATE"] || "",
        age:         item.AGE         ?? "",
        sample:      item.SAMPLE      ?? "",
        qno:         item.QNO         || "",
        mdcCode:     item.MDCcode     || "",
        mdc:         item.MDC         || "",
        rsm:         item.RSM         || "",
        asm:         item.ASM         || "",
        salesman:    item.SALESMAN    || "",
        salesOff:    item.SALESOFF    || "",
      }));

      // Remove exact-duplicate rows (every field identical) while keeping
      // legitimate rows that share the same Store/SKU but differ elsewhere.
      const dedupedMapped = dedupeExactRows(mapped);

      const HEADERS = [
        "SHOPNO","CUSTOMER","OUTLET NAME","ADDRESS","REGION","SUB REGION",
        "OUTLET TYPE","CLASS","BRAND","PACKAGING","SKU","SURVEY DATE",
        "MFD","EXPIRY DATE","AGE","SAMPLE","QNO","MDCcode","MDC","RSM","ASM",
        "SALESMAN","SALESOFF",
      ];
      const FIELDS = [
        "shopNo","customer","outletName","address","region","subRegion",
        "outletType","class","brand","packaging","sku","surveyDate",
        "mfd","expiryDate","age","sample","qno","mdcCode","mdc","rsm","asm",
        "salesman","salesOff",
      ];

      const headerStyle = {
        fill: { patternType: "solid", fgColor: { rgb: "C72127" } },
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };
      const dataStyle = {
        fill: { patternType: "solid", fgColor: { rgb: "FFFFFF" } },
        font: { color: { rgb: "2D3748" }, sz: 11 },
        alignment: { horizontal: "left", vertical: "center", wrapText: true },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };

      const wsData = [
        HEADERS.map((h) => ({ v: h, t: "s", s: headerStyle })),
        ...dedupedMapped.map((row) =>
          FIELDS.map((f) => ({
            v: row[f] !== undefined && row[f] !== null ? String(row[f]) : "",
            t: "s",
            s: dataStyle,
          }))
        ),
      ];

      const XLSX = await import("xlsx-js-style");
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws["!cols"] = HEADERS.map(() => ({ wch: 20 }));
      ws["!rows"] = [{ hpt: 30 }, ...dedupedMapped.map(() => ({ hpt: 22 }))];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Red Freshness Report");

      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([buf], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `RedFreshness_Report_${from}_to_${to}.xlsx`
      );


      if (!overrideFrom && !overrideTo) addToRecentReports(from, to, REPORT_TYPE_FRESHNESS);
    } catch (err) {
      console.error("Freshness export error:", err);
      alert("Failed to generate Red Freshness Excel: " + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (reportType === REPORT_TYPE_FRESHNESS) {
      handleFreshnessExport();
    }
    else if (reportType === REPORT_TYPE_STORES) {
      handleStoresExport(fromDateRef.current, toDateRef.current,setLoading); 
    }
    else if (reportType === REPORT_TYPE_SOVI) {
      handleSoviExport(fromDateRef.current, toDateRef.current, setLoading);
    }
    else if (reportType === REPORT_TYPE_MANUAL_AUDIT) {
      handleManualStoreAuditExport(fromDateRef.current, toDateRef.current, setLoading);
    }
    else {
      handleAllocationExport();
    }
  };
 
  const RadioOption = ({ value, label }) => {
    const selected = reportType === value;
    return (
      <Flex
        as="label"
        align="flex-start"
        gap={3}
        cursor="pointer"
        bg={selected ? "red.50" : "white"}
        px={4}
        py={3}
        borderRadius="lg"
        border="2px solid"
        borderColor={selected ? "red.500" : "gray.200"}
        transition="all 0.2s"
        _hover={{ borderColor: "red.400", bg: "red.50" }}
        w="100%"
        h="100%"
        minH="56px"
      >
        <input
          type="radio"
          name="reportType"
          value={value}
          checked={selected}
          onChange={() => setReportType(value)}
          style={{ display: "none" }}
        />
        {/* Custom radio dot */}
        <Box
          w="18px"
          h="18px"
          mt="1px"
          borderRadius="full"
          border="2px solid"
          borderColor={selected ? "red.500" : "gray.400"}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          bg="white"
        >
          {selected && (
            <Box w="9px" h="9px" borderRadius="full" bg="red.500" />
          )}
        </Box>
        <Text fontSize="sm" fontWeight={selected ? "bold" : "medium"} color={selected ? "red.700" : "gray.700"} lineHeight="1.4">
          {label.trim()}
        </Text>
      </Flex>
    );
  };

 
  return (
    <Box>
      <Container maxW="container.2xl" p="0" m="0">
        <Box bg="gray.50" overflow="hidden" mt="4rem">
          {/* Header */}
          <Box bg="white" borderRadius="2xl" mb={8} pt={5}>
            <VStack align="flex-start" spacing={2}>
              <Flex align="center" gap={3}>
                <Icon as={TbReportAnalytics} boxSize={8} color="red.600" />
                <Heading size="lg" color="gray.800">
                  Analytics Reports Dashboard
                </Heading>
              </Flex>
              <Text color="gray.500">
                Generate comprehensive Excel reports for RED Allocation Report (Red Score, Channels,
                Regionwise Score, BNL, BNTL and Outlet metrics) and Red Freshness Report. Select your desired
                date range to download complete analytics.
              </Text>
            </VStack>
          </Box>

          {/* Main Grid */}
          <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={4} mb={8}>
            {/* Left – Report Configuration */}
            <Box
              bg="white"
              borderRadius="2xl"
              p={{ base: 6, md: 6 }}
              boxShadow="xl"
              border="1px solid"
              borderColor="gray.200"
            >
              <Flex align="center" mb={6} gap={3}>
                <Icon as={FiCalendar} boxSize={6} color="red.600" />
                <Heading size="md" color="gray.800">Report Configuration</Heading>
              </Flex>

              <VStack spacing={8} align="stretch">
                {/* Date Range */}
                <Box
                  bg="red.50"
                  borderRadius="xl"
                  p={6}
                  border="2px solid"
                  borderColor="gray.200"
                  transition="all 0.3s"
                  _hover={{ borderColor: "red.300", bg: "red.50" }}
                >
                  <VStack align="stretch" spacing={4}>
                    <Flex align="center" gap={2}>
                      <Icon as={FiCalendar} color="red.600" />
                      <Text fontWeight="bold" color="gray.800">Select Reporting Period</Text>
                    </Flex>
                    <Flex direction={{ base: "column", md: "row" }} spacing={4} align="stretch">
                      <Box flex={1}>
                        <Text fontSize="sm" color="gray.600" mb={2} fontWeight="medium">From Date</Text>
                        <Box position="relative">
                          <Icon as={FiCalendar} position="absolute" left="4" top="50%" transform="translateY(-50%)" color="red.500" zIndex={1} />
                          <Input
                            type="date" value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            size="lg" w="80" pl="12" borderRadius="lg"
                            border="2px solid" borderColor="gray.300" bg="white"
                            fontSize="md" fontWeight="medium"
                            _hover={{ borderColor: "red.400" }}
                            _focus={{ borderColor: "red.500", boxShadow: "0 0 0 3px rgba(66,153,225,0.15)" }}
                          />
                        </Box>
                      </Box>
                      <Box flex={1}>
                        <Text fontSize="sm" color="gray.600" mb={2} fontWeight="medium">To Date</Text>
                        <Box position="relative">
                          <Icon as={FiCalendar} position="absolute" left="4" top="50%" transform="translateY(-50%)" color="red.500" zIndex={1} />
                          <Input
                            type="date" value={toDate} min={fromDate}
                            onChange={(e) => setToDate(e.target.value)}
                            size="lg" w="80" pl="12" borderRadius="lg"
                            border="2px solid" borderColor="gray.300" bg="white"
                            fontSize="md" fontWeight="medium"
                            _hover={{ borderColor: "red.400" }}
                            _focus={{ borderColor: "red.500", boxShadow: "0 0 0 3px rgba(66,153,225,0.15)" }}
                          />
                        </Box>
                      </Box>
                    </Flex>
                  </VStack>
                </Box>

                {/* Included Reports + Radio Selection */}
                <Box
                  bg="red.50"
                  borderRadius="xl"
                  p={6}
                  border="2px dashed"
                  borderColor="gray.300"
                >
                  <VStack align="stretch" spacing={5}>
                    <Flex align="center" gap={2}>
                      <Icon as={FiFileText} color="red.600" />
                      <Text fontWeight="bold" color="gray.800">Included Reports</Text>
                    </Flex>

                    {/* Report type radio buttons */}
                    <Box>
                      <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2} textTransform="uppercase" letterSpacing="0.05em">
                        Select Report Type
                      </Text>
                      <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={3}>
                        <RadioOption value={REPORT_TYPE_ALLOCATION}   label="Red Outlet Level Report" />
                        <RadioOption value={REPORT_TYPE_FRESHNESS}    label="Red Freshness Report" />
                        {!isCocaColaUser && (
                          <RadioOption value={REPORT_TYPE_STORES}       label="Stores Raw Data Report" />
                        )}
                        <RadioOption value={REPORT_TYPE_SOVI}         label="MT Report" />
                        {!isCocaColaUser && (
                          <RadioOption value={REPORT_TYPE_MANUAL_AUDIT} label="Manual Store Audit Report" />
                        )}
                      </SimpleGrid>
                    </Box>

                    {/* Report sheet badges – only for allocation */}
                    {reportType === REPORT_TYPE_ALLOCATION && (
                      <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                        {[
                          { name: "RED Score",         color: "green"  },
                          { name: "Channels",           color: "red"    },
                          { name: "Regionwise Score",   color: "purple" },
                          { name: "BNL",                color: "orange" },
                          { name: "BNTL",               color: "red"    },
                          { name: "Outlet Level Data",  color: "blue"   },
                        ].map((report, index) => (
                          <Flex
                            key={index}
                            align="center"
                            gap={2}
                            bg="white"
                            p={3}
                            borderRadius="lg"
                            border="1px solid"
                            borderColor="gray.200"
                          >
                            <Box w="3" h="3" borderRadius="full" bg={`${report.color}.500`} />
                            <Text fontSize="sm" fontWeight="medium" color="gray.700">{report.name}</Text>
                          </Flex>
                        ))}
                      </SimpleGrid>
                    )}

                    {/* Freshness sheet info */}
                    {reportType === REPORT_TYPE_FRESHNESS && (
                      <Flex align="center" gap={2} bg="white" p={3} borderRadius="lg" border="1px solid" borderColor="gray.200">
                        <Box w="3" h="3" borderRadius="full" bg="red.500" />
                        <Text fontSize="sm" fontWeight="medium" color="gray.700">
                          Red Freshness Report (SKU / Expiry data)
                        </Text>
                      </Flex>
                    )}

                    {reportType === REPORT_TYPE_STORES && (
                      <Flex align="center" gap={2} bg="white" p={3} borderRadius="lg" border="1px solid" borderColor="gray.200">
                        <Box w="3" h="3" borderRadius="full" bg="purple.500" />
                        <Text fontSize="sm" fontWeight="medium" color="gray.700">
                          Stores Raw Data (SKU, shelf & transaction columns)
                        </Text>
                      </Flex>
                    )}
                  {reportType === REPORT_TYPE_SOVI && (
                    <Flex align="center" gap={2} bg="white" p={3} borderRadius="lg" border="1px solid" borderColor="gray.200">
                      <Box w="3" h="3" borderRadius="full" bg="purple.500" />
                      <Text fontSize="sm" fontWeight="medium" color="gray.700">
                        MT Report (Cooler, Purity, Facings & SKU data)
                      </Text>
                    </Flex>
                  )}

                  {reportType === REPORT_TYPE_MANUAL_AUDIT && (
                    <Flex align="center" gap={2} bg="white" p={3} borderRadius="lg" border="1px solid" borderColor="gray.200">
                      <Box w="3" h="3" borderRadius="full" bg="orange.500" />
                      <Text fontSize="sm" fontWeight="medium" color="gray.700">
                        Manual Store Audit
                      </Text>
                    </Flex>
                  )}

                  </VStack>
                </Box>
              </VStack>
            </Box>

            {/* Right – Download Panel */}
            <Box
              bgGradient="linear(135deg, #C41E3A 0%, #F40009 50%, #DC2626 100%)"
              borderRadius="2xl"
              p={{ base: 6, md: 6 }}
              boxShadow="0 20px 60px rgba(244, 0, 9, 0.4), 0 0 0 1px rgba(255,255,255,0.1) inset"
              color="white"
              display="flex"
              flexDirection="column"
              justifyContent="center"
              position="relative"
              overflow="hidden"
              _before={{
                content: '""',
                position: "absolute", top: 0, left: 0, right: 0, height: "50%",
                background: "linear-gradient(180deg, rgba(255,255,255,0.2) 0%, transparent 100%)",
                pointerEvents: "none",
              }}
              sx={{ backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)" }}
            >
              <VStack spacing={8} align="center" textAlign="center" position="relative" zIndex={1}>
                <Icon as={BsGraphUp} boxSize={16} opacity="0.95" filter="drop-shadow(0 4px 8px rgba(0,0,0,0.2))" />

                <Box>
                  <Heading size="lg" mb={3} fontWeight="800" letterSpacing="0.02em"
                    textShadow="0 2px 8px rgba(0,0,0,0.3), 0 0 20px rgba(255,255,255,0.1)">
                    Ready to Download
                  </Heading>
                  <Text fontSize="md" fontWeight="600" opacity="0.95"
                    textShadow="0 1px 4px rgba(0,0,0,0.2)" lineHeight="1.6">
                    {reportType === REPORT_TYPE_FRESHNESS
                      ? <>Generate your <strong style={{ color: "#FFE5E5" }}>Red Freshness</strong> report with SKU &amp; expiry data</>
                      : <>Generate your <strong style={{ color: "#FFE5E5" }}>comprehensive analytics</strong> report with all metrics included</>
                    }
                  </Text>
                </Box>

                {/* Currently selected report type badge */}
                <Box
                  bg="rgba(255,255,255,0.15)"
                  borderRadius="lg"
                  px={4} py={2}
                  border="1px solid rgba(255,255,255,0.3)"
                  w="100%"
                  textAlign="center"
                >
                  <Text fontSize="sm" fontWeight="semibold" opacity="0.9">
                  {reportType === REPORT_TYPE_FRESHNESS
                    ? "🔴 Red Freshness Report"
                    : reportType === REPORT_TYPE_STORES
                    ? "🏪 Stores Raw Data Report"
                    : reportType === REPORT_TYPE_SOVI
                    ? "📈 MT Report"
                    : reportType === REPORT_TYPE_MANUAL_AUDIT
                    ? "🗂️ Manual Store Audit Report"
                    : "📊 Red Outlet Level Report"}
                  </Text>
                </Box>

                <Button
                  onClick={handleExport}
                  isLoading={loading}
                  loadingText="Generating Report..."
                  size="lg"
                  bg="white"
                  color="red.600"
                  leftIcon={<FiDownload />}
                  px={12} py={6}
                  fontWeight="bold"
                  fontSize="lg"
                  borderRadius="xl"
                  boxShadow="0 10px 30px rgba(244,0,9,0.3), 0 0 0 1px rgba(244,0,9,0.1) inset"
                  _hover={{
                    transform: "translateY(-3px)",
                    boxShadow: "0 15px 40px rgba(244,0,9,0.4), 0 0 0 1px rgba(244,0,9,0.2) inset",
                    bg: "red.50", color: "red.700",
                  }}
                  _active={{ transform: "translateY(-1px)" }}
                  transition="all 0.3s cubic-bezier(0.4,0,0.2,1)"
                  width="100%"
                >
                  Download Excel Report
                </Button>
              </VStack>
            </Box>
          </Grid>

          {/* Recent Reports */}
          <Box
            bg="white"
            borderRadius="2xl"
            p={{ base: 6, md: 8 }}
            boxShadow="xl"
            border="1px solid"
            borderColor="gray.200"
          >
            <Flex align="center" justify="space-between" mb={6}>
              <Flex align="center" gap={3}>
                <Icon as={FiDownload} boxSize={6} color="red.600" />
                <Heading size="md" color="gray.800">Recent Reports</Heading>
              </Flex>
              <Badge colorScheme="red" variant="subtle" px={4} py={1} borderRadius="full">
                Last {recentReports.length} reports
              </Badge>
            </Flex>

            {recentReports.length === 0 ? (
              <Center py={8}>
                <VStack spacing={2}>
                  <Icon as={FiFileText} boxSize={8} color="gray.300" />
                  <Text color="gray.400" fontSize="sm">No reports generated yet</Text>
                </VStack>
              </Center>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {recentReports.map((report, index) => (
                  <Box
                    key={index}
                    bg="gray.50"
                    borderRadius="lg"
                    p={4}
                    border="1px solid"
                    borderColor="gray.200"
                    transition="all 0.2s"
                    _hover={{ borderColor: "red.300", transform: "translateY(-2px)", boxShadow: "md" }}
                  >
                    <Flex justify="space-between" align="start" mb={3}>
                      <VStack align="start" spacing={1} flex={1} minW={0}>
                        <Text fontWeight="bold" color="gray.800" fontSize="sm" noOfLines={2}>
                          {report.month}
                        </Text>
                        <Flex align="center" gap={2}>
                                              <Badge
                        colorScheme={
                          report.reportType === REPORT_TYPE_FRESHNESS    ? "orange"
                          : report.reportType === REPORT_TYPE_SOVI       ? "purple"
                          : report.reportType === REPORT_TYPE_STORES     ? "green"
                          : report.reportType === REPORT_TYPE_MANUAL_AUDIT ? "yellow"
                          : "blue"
                        }
                      >
                        {report.reportType === REPORT_TYPE_FRESHNESS    ? "Freshness"
                        : report.reportType === REPORT_TYPE_SOVI        ? "SOVI"
                        : report.reportType === REPORT_TYPE_STORES      ? "Stores"
                        : report.reportType === REPORT_TYPE_MANUAL_AUDIT ? "Manual Audit"
                        : "Allocation"}
                      </Badge>
                          {report.size !== "—" && (
                            <Text fontSize="xs" color="gray.500">{report.size}</Text>
                          )}
                        </Flex>
                      </VStack>
                      <Badge colorScheme="green" variant="subtle" ml={2} flexShrink={0}>
                        {report.status}
                      </Badge>
                    </Flex>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="red"
                      width="100%"
                      leftIcon={<FiDownload />}
                      isLoading={loading}
                      onClick={async() =>{
                         setLoadingIndex(index)
                       await handleDownloadReport(report)
                       setLoadingIndex(null)
                       }}
                    >
                      Download Again
                    </Button>
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </Box>
         
          {/* Footer */}
          <Box mt={8} textAlign="center">
            <Text fontSize="sm" color="gray.500" maxW="2xl" mx="auto">
              Reports are generated in real-time and may take a few moments to
              process depending on the selected date range's data volume.
            </Text>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}